import { NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NgModel } from '@angular/forms';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize, interval, Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { TEMPLATE_CONSTANT } from '../../../constants/template.constant';
import { TRANSLATION_TEMPLATES } from '../../../constants/translation-templates-constants';
import { OTP_COMPLEXITY, OTP_VERIFICATION_TYPE, WIDGET_CONSTANTS, WIDGET_PROTECTION_TYPE } from '../../../constants/widget-constants';
import { CustomerDbModel } from '../../../db-models/appointment-custom-fields-dao';
import { CustomBookingMessageTemplate } from '../../../db-models/widget-conf-dao';
import { GlobalObjects, Partner } from '../../../models/global';
import { WidgetColorConf } from '../../../models/widget-color.model';
import { WidgetConf } from '../../../models/widget-conf';
import { CustomersService } from '../../../services/customers.service';
import { HelperService } from '../../../services/helper.service';
import { PartnerService } from '../../../services/partner.service';
import { AlertComponent } from '../../../shared/components/alert/alert.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CalioMeetingTemplatePipe } from '../../../shared/pipes/calio-meeting-template.pipe';
import { TranslationPipe } from '../../../shared/pipes/translation.pipe';
import { TrustHtmlPipe } from '../../../shared/pipes/trust-html.pipe';
import { OtpInputComponent } from '../otp-input/otp-input.component';

const modules = [NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownItem, FormsModule, TranslateModule, NgTemplateOutlet];
const components = [AlertComponent, ButtonComponent, OtpInputComponent];
const pipes = [TranslationPipe, CalioMeetingTemplatePipe, TrustHtmlPipe];

@Component({
  selector: 'app-password-protection',
  imports: [...modules, ...components, ...pipes],
  templateUrl: './password-protection.component.html',
  styleUrl: './password-protection.component.scss'
})
export class PasswordProtectionComponent {

  private translateService = inject(TranslateService);
  private partnerService = inject(PartnerService);
  private destroyRef = inject(DestroyRef);
  private helperService = inject(HelperService);
  private customersService = inject(CustomersService);

  protected readonly deployUrl = environment.deployUrl;
  protected readonly languages = WIDGET_CONSTANTS.LANGUAGES;
  protected readonly otpVerificationType = OTP_VERIFICATION_TYPE;
  protected readonly templateContent = TRANSLATION_TEMPLATES;
  protected readonly widgetPasswordType = WIDGET_PROTECTION_TYPE;
  protected readonly templateConstant = TEMPLATE_CONSTANT;
  protected readonly otpComplexity = OTP_COMPLEXITY;
  private readonly OTP_TIMER_DURATION = 60 * 2;

  private _customer: CustomerDbModel;
  private otpTimerSubscription: Subscription | null = null;
  protected _widgetTemplates = Array<CustomBookingMessageTemplate>();
  protected blockForTimeMinutes = 0;
  protected blockForTimeSeconds = 0;
  protected customerEmail: string;
  protected customerMobile: string;
  protected customerPhone: string;
  protected enteredOtp: string | null = null;
  protected errorMessage = '';
  protected invalidPassword = false;
  protected isOtpSent = false;
  protected isOtpTimerActive = false;
  protected isRateLimitError = false;
  protected isSendOtpLoading = false;
  protected isTemplateLoading = false;
  protected isVerifyOtpLoading = false;
  protected isVerifyPasswordLoading = false;
  protected otpTimerSeconds = 0;

  @Input() protected supportedWidgetLanguages: string[] = [];
  @Input() protected langSwitcher = true;
  @Input() protected lang: string;
  @Input() protected globals: GlobalObjects;
  @Input() protected showDevAlert = false;
  @Input() protected widgetColorConf: WidgetColorConf;
  @Input() protected partner: Partner;
  @Input() protected widgetConf: WidgetConf;
  @Input() set customer(value: CustomerDbModel) {
    this._customer = value;
    this.customerEmail = this.helperService.maskEmail(this._customer?.email || '');
    this.customerMobile = this.helperService.maskMobile(this._customer?.mobile || '');
    this.customerPhone = this.helperService.maskMobile(this._customer?.phone || '');
  }
  @Input() set widgetTemplates(value: CustomBookingMessageTemplate[]) {
    if (value?.length) {
      this._widgetTemplates = value;
      this.isTemplateLoading = false;
    } else {
      this.isTemplateLoading = true;
    }
  }

  @Output() passwordVerified = new EventEmitter<void>();


  constructor() {
    this.translateService.onLangChange.subscribe(lang => this.lang = lang.lang);
  }

  protected switchLanguage(language: string): void {
    this.lang = language;
    this.translateService.use(language);
    this.globals.user_selected_language = language;
  }

  protected submitPassword(password: NgModel): void {
    if (password.invalid) {
      password.control.markAsTouched();
      return;
    }

    this.isVerifyPasswordLoading = true;
    this.invalidPassword = this.isRateLimitError = false;
    this.errorMessage = undefined;
    this.partnerService.verifyWidgetPassword({ password: password?.value?.trim() })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isVerifyPasswordLoading = false)
      )
      .subscribe({
        next: response => {
          if (response?.success) {
            this.passwordVerified.emit();
          } else {
            this.invalidPassword = response?.code === HttpStatusCode.Unauthorized || true;
          }
        },
        error: () => this.invalidPassword = true
      });
  }

  protected submitOtp(): void {
    if (
      !this.enteredOtp ||
      ([OTP_COMPLEXITY.LOW].includes(this.widgetConf?.otp_complexity) && this.enteredOtp.length !== 4) ||
      ([OTP_COMPLEXITY.MEDIUM, OTP_COMPLEXITY.HIGH].includes(this.widgetConf?.otp_complexity) && this.enteredOtp.length !== 6)
    ) {
      // this.errorMessage = 'error_messages.invalid_otp';
      return;
    }

    this.invalidPassword = this.isRateLimitError = false;
    this.errorMessage = undefined;
    this.isVerifyOtpLoading = true;
    this.customersService.verifyOtp({ otp: this.enteredOtp, customer_uuid: this._customer?.uuid })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isVerifyOtpLoading = false)
      )
      .subscribe({
        next: () => this.passwordVerified.emit(),
        error: (error: HttpErrorResponse) => this.handelError(error)
      });
  }

  protected sendOtp(): void {
    if (this.isSendOtpLoading) return;
    if (this.isOtpTimerActive) return;
    this.isSendOtpLoading = true;
    this.invalidPassword = this.isRateLimitError = false;
    this.errorMessage = undefined;
    const body = { customer_uuid: this._customer?.uuid };
    if (this.isOtpSent) {
      body['retry'] = true;
    }
    this.customersService.sendOtp(body)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isSendOtpLoading = false)
      )
      .subscribe({
        next: () => {
          this.isOtpSent = true;
          this.startOtpTimer();
        },
        error: (error: HttpErrorResponse) => this.handelError(error)
      });
  }

  protected handelError(error: HttpErrorResponse): void {
    const errorIdentifier = error?.error?.identifier || error?.error?.error?.identifier;
    this.errorMessage = undefined;
    this.enteredOtp = null;
    if (['CUSTOMER_UUID_MISSING', 'INVALID_MOBILE_NUMBER', 'CUSTOMER_NOT_FOUND', 'OTP_MISSING', 'OTP_NOT_ENABLED'].includes(errorIdentifier)) {
      this.errorMessage = 'error_messages.invalid_configuration';
    } else if (errorIdentifier === 'INVALID_OTP') {
      this.invalidPassword = true;
    } else if (['REACHED_MAX_LIMIT'].includes(errorIdentifier)) {
      this.isOtpSent = false;
      this.isOtpTimerActive = false;
      this.errorMessage = 'error_messages.reached_max_limit';
    } else if (['TOO_MANY_REQUESTS'].includes(errorIdentifier)) {
      this.handleRateLimitError(error);
    } else {
      this.errorMessage = 'error_messages.something_went_wrong';
    }
  }

  private startOtpTimer(): void {
    // Destroy any existing timer subscription before starting a new one
    if (this.otpTimerSubscription) {
      this.otpTimerSubscription.unsubscribe();
      this.otpTimerSubscription = null;
    }

    this.otpTimerSeconds = this.OTP_TIMER_DURATION;
    this.isOtpTimerActive = true;

    this.otpTimerSubscription = interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.otpTimerSeconds > 0) {
          this.otpTimerSeconds--;
        } else {
          this.isOtpTimerActive = false;
          // Clean up subscription when timer completes
          if (this.otpTimerSubscription) {
            this.otpTimerSubscription.unsubscribe();
            this.otpTimerSubscription = null;
          }
        }
      });
  }

  protected getFormattedTimer(): string {
    const minutes = Math.floor(this.otpTimerSeconds / 60);
    const seconds = this.otpTimerSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  protected updateOtp(otp: string) {
    this.enteredOtp = otp;
    this.submitOtp();
  }

  private handleRateLimitError(error: HttpErrorResponse): void {
    this.blockForTimeMinutes = this.blockForTimeSeconds = 0;
    if (error.headers.get('Retry-After')) {
      const retryAfter = +error.headers.get('Retry-After');
      if (retryAfter > 60) {
        this.blockForTimeMinutes = Math.floor(retryAfter / 60);
      } else {
        this.blockForTimeSeconds = +error.headers.get('Retry-After');
      }
    }
    this.isRateLimitError = true;
  }
}
